import { Injector, computed, inject, signal } from "@angular/core";
import { CommonForm, CommonOptionsFieldConfig, CommonSelectField, CommonTextField, CommonTextFieldConfig, HOURNAMES_MAP, STANDARD_FOUR_TIME_ZONE_ID } from "@eforall/common";
import { AccTeamDialogDataService } from "../acc-team-dialog-data.service";

export interface AccTeamFormValues {
	name: string | undefined,
	googleMeetCode: string | undefined,
	dayIndex: string | undefined,
	startTimeIndex: string | undefined,
	meetingDetails: string | undefined,
}

export class DetailsPartForm {

	private readonly dialogDataService = inject(AccTeamDialogDataService);
	public readonly form = new CommonForm<AccTeamFormValues>(inject(Injector));

	readonly name = computed<CommonTextField>(() => ({
		config: signal<CommonTextFieldConfig>({
			label: 'Team Name',
			isRequired: false,
			min: 0,
			max: 200, // it's a text in the db
			fixCase: 'sentence-case',
			multiLine: false,
		}),
		formFieldName: 'name',
		form: this.form,
		actualValue: computed(() => this.dialogDataService.dialogData()?.name ?? ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
	}));



	readonly googleMeetCode = computed<CommonTextField>(() => ({
		config: signal<CommonTextFieldConfig>({
			label: 'Team Google Meet Code',
			isRequired: false,
			min: 0,
			max: 60,
			fixCase: 'none',
			multiLine: false,
		}),
		formFieldName: 'googleMeetCode',
		form: this.form,
		actualValue: computed(() => this.dialogDataService.dialogData()?.googleMeetCode ?? ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
	}));


	// changing the value to string , as the default value of select field<number> is 0
	// which is causing issue if the actual value is 0
	private dayIndexOptions = [
		{ value: '0', text: 'Monday' },
		{ value: '1', text: 'Tuesday' },
		{ value: '2', text: 'Wednesday' },
		{ value: '3', text: 'Thursday' },
		{ value: '4', text: 'Friday' },
		{ value: '5', text: 'Saturday' },
		{ value: '6', text: 'Sunday' },
	];
	readonly dayIndex = computed<CommonSelectField<string | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<string>>({
			label: 'Meeting Day',
			isRequired: false,
			options: this.dayIndexOptions,
			isEnabled: computed(() => this.dialogDataService.canEdit()),
		}),
		formFieldName: 'dayIndex',
		form: this.form,
		actualValue: computed(() => this.dialogDataService.dialogData()?.meetingInfo?.dayIndex.toString()),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
	}));


	// changing the value to string , as the default value of select field<number> is 0
	// which is causing issue if the actual value is 0
	private timeOptions() {
		const hourOptions = HOURNAMES_MAP[STANDARD_FOUR_TIME_ZONE_ID.Eastern];

		return hourOptions.map((hourName, index) => ({ value: index.toString(), text: `${hourName} ET` }));
	};

	// Problem: isRequired is a boolean, not a signal. if isRequired is isTimeRequired() - When isTimeRequired changes,
	// the entire timeIndex field gets recreated, losing its state (pendingValue, error, etc.)
	// to test: try reverting the form after setting a time - the timeIndex field should retain its pendingValue
	private isTimeRequired = computed(() => {
		const dayIndex = this.dayIndex();
		const value = dayIndex.pendingValue() ?? dayIndex.actualValue();

		return !!value;
	});
	readonly timeIndex = computed<CommonSelectField<string | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<string>>({
			label: 'Meeting Time',
			isRequired: false,
			options: this.timeOptions(),
			isEnabled: computed(() => this.dialogDataService.canEdit()),
		}),
		formFieldName: 'startTimeIndex',
		form: this.form,
		actualValue: computed(() => this.dialogDataService.dialogData()?.meetingInfo?.startTimeIndex.toString()),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
	}));


	readonly meetingDetails = computed<CommonTextField>(() => ({
		config: signal<CommonTextFieldConfig>({
			label: 'Additional team Meeting Details',
			isRequired: false,
			min: 0,
			max: 100,
			fixCase: 'sentence-case',
			multiLine: true,
		}),
		formFieldName: 'meetingDetails',
		form: this.form,
		actualValue: computed(() => this.dialogDataService.dialogData()?.meetingDetails ?? ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
	}));

}